import React, { useState, useRef, useEffect } from "react";
import "./DetailedPaymentReport.css";
import { FaFilter, FaCheckCircle, FaExclamationCircle, FaTimes } from "react-icons/fa";
import CategoryRecordsModal from "./CategoryRecordsModal";

const DetailedPaymentReport = ({ data, title, type, fromDate, toDate }) => {
  const [selectedParticulars, setSelectedParticulars] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeCategoryRecord, setActiveCategoryRecord] = useState(null);
  const filterRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!data || data.length === 0) return null;

  const particularsOptions = Array.from(new Set(data.map((item) => item.particulars || item.invoiceName)));

  const filteredData =
    selectedParticulars.length > 0
      ? data.filter((item) => selectedParticulars.includes(item.particulars || item.invoiceName))
      : data;

  const toggleParticular = (p) => {
    if (selectedParticulars.includes(p)) {
      setSelectedParticulars(selectedParticulars.filter((item) => item !== p));
    } else {
      setSelectedParticulars([...selectedParticulars, p]);
    }
  };

  const clearAll = () => setSelectedParticulars([]);

  const isPending = type === 'pending' || title.toLowerCase().includes("pending");

  const totalReportAmount = filteredData.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <>
      <div className={`detailed-report-block ${isPending ? 'pending-block' : 'completed-block'}`}>
        <div className="table-section-header">
          <div className="section-title-group">
            {isPending ? (
              <FaExclamationCircle size={15} color="#e11d48" />
            ) : (
              <FaCheckCircle size={15} color="#16a34a" />
            )}
            <h4 className={`table-title ${isPending ? 'pending-title' : 'completed-title'}`}>
              {title}
            </h4>
            <span className="count-pill-badge">
              {filteredData.length} {filteredData.length === 1 ? 'Category' : 'Categories'}
            </span>
          </div>

          <div className="section-total-badge">
            Total: <strong>₹ {totalReportAmount.toLocaleString()}</strong>
          </div>
        </div>

        <div className="table-responsive">
          <table className="report-data-table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>SR NO.</th>
                <th style={{ position: "relative" }} ref={filterRef}>
                  <div
                    className="particulars-filter-trigger"
                    onClick={() => setFilterOpen(!filterOpen)}
                    title="Filter by Particulars"
                  >
                    <span>PARTICULARS</span>
                    <FaFilter size={10} color={selectedParticulars.length > 0 ? "#2563eb" : "#64748b"} />
                  </div>

                  {selectedParticulars.length > 0 && !filterOpen && (
                    <div className="active-filter-tag">
                      Filtered ({selectedParticulars.length})
                      <button className="clear-filter-x" onClick={clearAll} title="Clear filter">
                        <FaTimes size={9} />
                      </button>
                    </div>
                  )}

                  {filterOpen && (
                    <div className="filter-dropdown-wrapper">
                      <div className="filter-dropdown-header">
                        <span>Filter Categories</span>
                        <button className="clear-all-link" onClick={clearAll}>Clear All</button>
                      </div>
                      <ul className="filter-dropdown-list">
                        <li onClick={clearAll} className={selectedParticulars.length === 0 ? 'selected-item' : ''}>
                          <input
                            type="checkbox"
                            checked={selectedParticulars.length === 0}
                            readOnly
                          />{" "}
                          All Particulars
                        </li>
                        {particularsOptions.map((p, index) => (
                          <li
                            key={index}
                            onClick={() => toggleParticular(p)}
                            className={selectedParticulars.includes(p) ? 'selected-item' : ''}
                          >
                            <input
                              type="checkbox"
                              checked={selectedParticulars.includes(p)}
                              readOnly
                            />{" "}
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </th>

                <th style={{ width: '120px', textAlign: 'center' }}>RECORD COUNT</th>
                <th style={{ width: '160px', textAlign: 'right' }}>TOTAL AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr
                    key={index}
                    className="report-row-item clickable-report-row"
                    onClick={() => setActiveCategoryRecord(item)}
                    title="Click to view detailed itemized transactions"
                  >
                    <td>
                      <span className="sr-num-pill">{String(index + 1).padStart(2, '0')}</span>
                    </td>
                    <td>
                      <span className="particular-name">{item.particulars || item.invoiceName}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="count-number-pill">{item.count}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className={`amount-display ${isPending ? 'pending-amt' : 'completed-amt'}`}>
                        ₹ {(item.amount || 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="empty-report-msg">
                    No records match selected filter criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Category Detailed Records Breakdown Modal */}
      <CategoryRecordsModal
        isOpen={!!activeCategoryRecord}
        onClose={() => setActiveCategoryRecord(null)}
        categoryData={activeCategoryRecord}
        categoryType={type}
        fromDate={fromDate}
        toDate={toDate}
      />
    </>
  );
};

export default DetailedPaymentReport;
